import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import styles from './DomesticContractInfoSubmitForm.module.scss'
import { useForm, Controller } from 'react-hook-form'
import ToggleButtonGroupController from '../../components/ToggleButtonGroup/ToggleButtonGroupController'
import SimpleInputController from '../../components/simpleInput/SimpleInputController'
import IncentiveModal from '../onboarding/IncentiveModal'
import { formatIncentiveTaxOptionLabel } from '../../utils/incentiveDisplay'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'

const DomesticContractInfoSubmitForm = forwardRef(({ onValidationChange }, ref) => {
	// 숫자 포맷팅 함수
	const formatNumber = (value) => {
		if (!value) return ''
		return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}
	const { control, handleSubmit, watch, setValue, formState, reset } = useForm({
		defaultValues: {
			domestic: {
				applyTax: 'exclude', // 세전 (왼쪽 버튼)
				payPeriod: 'weekly', // 주급 (왼쪽 버튼)
				cutPass: '',
				cutOut: '',
				sponsorCap: true, // 착용 (왼쪽 버튼)
				isTourJejudo: true,
				jejudoSupport: '',
				incentiveSettings: [], // 인센티브 설정
				incentiveTaxOption: 'amountDisplayed', // 인센티브 택스 옵션
			},
		},
	})
	const [isModalOpen, setIsModalOpen] = useState(false)

	// 이전 데이터 로드 - useEffect로 한 번만 실행
	useEffect(() => {
		try {
			const previousData =
				sessionStorage.getItem('DomesticContractPreviousData') ||
				sessionStorage.getItem('ReNegotiationDomesticContractPreviousData')
			if (previousData) {
				const parsedData = JSON.parse(previousData)
				console.log('국내 계약 이전 데이터 로드:', parsedData)

				// 각 필드를 개별적으로 업데이트
				setValue(
					'domestic.applyTax',
					parsedData.isSalaryTaxIncluded ? 'include' : 'exclude',
				)
				setValue(
					'domestic.payPeriod',
					parsedData.payrollCycles === 'weekly' ? 'weekly' : 'oneTime',
				)
				setValue('domestic.cutPass', formatNumber(parsedData.weeklySalaryCutThrough))
				setValue('domestic.cutOut', formatNumber(parsedData.weeklySalaryCutFail))
				setValue('domestic.sponsorCap', parsedData.wearSponsoredHat)
				setValue('domestic.isTourJejudo', parsedData.jejuTournamentParticipation)
				setValue(
					'domestic.jejudoSupport',
					formatNumber(parsedData.expenseReimbursementJeju),
				)
				setValue(
					'domestic.incentiveSettings',
					parsedData.incentives
						.sort((a, b) => a.startRank - b.startRank)
						.map((setting, index) => {
							return {
								id: index + 1,
								start: setting.startRank,
								percentage: setting.percentage,
								end: setting.endRank === 0 ? 'CUT' : setting.endRank,
							}
						}),
				)
				setValue(
					'domestic.incentiveTaxOption',
					parsedData.prizeType === 'displayed' ? 'amountDisplayed' : 'amountReceived',
				)

				console.log('폼 필드 업데이트 완료')
			}
		} catch (error) {
			console.error('이전 데이터 로드 실패:', error)
		}
	}, [setValue])

	// 토글 옵션들
	const applyTaxOption = [
		{ value: 'exclude', label: '세전' },
		{ value: 'include', label: '세후' },
	]

	const payPeriodOption = [
		{ value: 'weekly', label: '주급' },
		{ value: 'oneTime', label: '일시불' },
	]

	const sponsorCapOption = [
		{ value: true, label: '착용' },
		{ value: false, label: '미착용' },
	]

	const jejudoOption = [
		{ value: true, label: '지원' },
		{ value: false, label: '미지원' },
	]

	// 현재 폼 데이터
	const activeData = watch('domestic')
	const isTourJejudo = watch('domestic.isTourJejudo')
	const jejudoSupport = watch('domestic.jejudoSupport')
	const incentiveSettings = watch('domestic.incentiveSettings')

	// validation 상태를 상위 컴포넌트로 전달
	useEffect(() => {
		const isValid =
			formState.isValid &&
			incentiveSettings &&
			incentiveSettings.length > 0 &&
			(isTourJejudo === false ||
				(isTourJejudo === true && jejudoSupport && jejudoSupport.trim() !== ''))

		onValidationChange && onValidationChange('domestic', isValid)
	}, [formState.isValid, incentiveSettings, isTourJejudo, jejudoSupport])

	// 가격 입력 처리 함수
	const handlePriceInputChange = (e, fieldOnChange) => {
		// 숫자만 입력받도록 처리
		const numericValue = e.target.value.replace(/[^0-9]/g, '')

		// 천원 단위 콤마 적용
		const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

		// 필터링된 값으로 폼 업데이트
		fieldOnChange(formattedValue)
	}

	// 모달 열기 함수
	const handleModalOpen = () => {
		setIsModalOpen(true)
	}

	// 폼 제출 처리
	const onSubmit = (data) => {
		console.log('폼 데이터:', data)
		// 여기에 제출 로직 추가
	}

	// ref를 통해 부모 컴포넌트에서 호출할 수 있는 메서드들
	useImperativeHandle(ref, () => ({
		getFormData: () => {
			return {
				applyTax: activeData?.applyTax,
				payPeriod: activeData?.payPeriod,
				periodSalary: activeData?.periodSalary
					? activeData.periodSalary.replace(/,/g, '')
					: '',
				cutPass: activeData?.cutPass ? activeData.cutPass.replace(/,/g, '') : '',
				cutOut: activeData?.cutOut ? activeData.cutOut.replace(/,/g, '') : '',
				sponsorCap: activeData?.sponsorCap,
				isTourJejudo: activeData?.isTourJejudo,
				jejudoSupport: activeData?.jejudoSupport
					? activeData.jejudoSupport.replace(/,/g, '')
					: '',
				incentiveSettings: activeData?.incentiveSettings,
				incentiveTaxOption: activeData?.incentiveTaxOption,
			}
		},
	}))

	return (
		<div className={styles.contractInfoSubmitFormWrapper}>
			<div className={styles.contractInfoSubmitFormHeader}>
				<div className={styles.domesticIcon}>국내</div>
				<span className={styles.contractInfoSubmitFormTitle}>계약 정보를 작성해주세요</span>
			</div>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className={styles.contractInfoSubmitFormContainer}>
				<div className={styles.applyTax}>
					<ToggleButtonGroupController
						control={control}
						name="domestic.applyTax"
						toggleFieldLabel="임금 세금 포함 여부"
						isNoticeNeed={true}
						isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
						toggleOption={applyTaxOption}
						styleMode={styles}
					/>
				</div>
				<div className={styles.payPeriod}>
					<ToggleButtonGroupController
						control={control}
						name="domestic.payPeriod"
						toggleFieldLabel="임금 지급 주기"
						toggleOption={payPeriodOption}
						styleMode={styles}
					/>
				</div>
				<div className={styles.cutPass}>
					<SimpleInputController
						control={control}
						name="domestic.cutPass"
						rules={{ required: '컷통과 금액을 입력해주세요' }}
						inputLabel="컷통과"
						inputPlaceholder="컷통과 시 지급할 금액을 입력해주세요"
						onChange={handlePriceInputChange}
					/>
				</div>
				<div className={styles.cutOut}>
					<SimpleInputController
						control={control}
						name="domestic.cutOut"
						rules={{ required: '컷탈락 금액을 입력해주세요' }}
						inputLabel="컷탈락"
						inputPlaceholder="컷탈락 시 지급할 금액을 입력해주세요"
						onChange={handlePriceInputChange}
					/>
				</div>
				{/* <div className={styles.incentive}>
					<span>인센티브</span>
					<button type="button" onClick={handleModalOpen}>
						입력하기
					</button>
				</div> */}
				<div className={styles.incentive}>
					<div className={styles.incentiveInfo}>
						인센티브
						<InformationCircleIcon
							className={styles.noticeIcon}
							data-tooltip-id="my-tooltip2"
							data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
						/>
						<Tooltip id="my-tooltip2" place="top" className={styles.tooltip} />
					</div>
					<button type="button" onClick={handleModalOpen}>
						입력하기
					</button>
				</div>

				<Controller
					name="domestic.incentiveSettings"
					control={control}
					render={({ field }) => <input type="hidden" {...field} />}
				/>
				{activeData?.incentiveSettings && activeData?.incentiveSettings.length > 0 && (
					<div className={styles.incentiveDisplaySection}>
						<div className={styles.incentiveTaxDisplay}>
							{formatIncentiveTaxOptionLabel(activeData.incentiveTaxOption)}
						</div>
						{activeData.incentiveSettings.map((setting) => {
							if (setting.percentage === null && setting.end === null) return null
							const displayRank =
								setting.id === 1
									? '우승 : '
									: setting.start +
										(setting.end !== null ? ` ~ ${setting.end} :  ` : '~ ')
							const displayPercentage =
								setting.percentage !== null ? `  ${setting.percentage}%` : '미입력'
							return (
								<div key={setting.id} className={styles.incentiveDisplayItem}>
									<div>{displayRank}</div>
									<div>{displayPercentage}</div>
								</div>
							)
						})}
					</div>
				)}
				<div className={styles.overseasTour}>
					<ToggleButtonGroupController
						control={control}
						name="domestic.isTourJejudo"
						toggleFieldLabel="제주대회 출전시"
						toggleOption={jejudoOption}
						isNoticeNeed={true}
						isNoticeNeedText={`제주대회 출전시 : 세후 금액으로 3.3% 제외된 금액`}
						styleMode={styles}
					/>
				</div>
				{activeData.isTourJejudo && (
					<SimpleInputController
						control={control}
						name="domestic.jejudoSupport"
						rules={{
							required: activeData.isTourJejudo
								? '제주대회 지원금액을 입력해주세요'
								: false,
						}}
						inputPlaceholder="제주대회 지원금액을 입력해주세요"
						onChange={handlePriceInputChange}
					/>
				)}
				<div className={styles.sponsorCap}>
					<ToggleButtonGroupController
						control={control}
						name="domestic.sponsorCap"
						toggleFieldLabel="선수 스폰서 모자 착용"
						toggleOption={sponsorCapOption}
						styleMode={styles}
					/>
				</div>
			</form>

			{/* 인센티브 모달 */}
			<IncentiveModal
				isModalOpen={isModalOpen}
				onFinish={(incentiveTaxOption, incentiveSettings) => {
					setValue('domestic.incentiveSettings', incentiveSettings)
					setValue('domestic.incentiveTaxOption', incentiveTaxOption)
					setIsModalOpen(false)
				}}
				onReturn={() => setIsModalOpen(false)}
				initialSettings={activeData?.incentiveSettings}
			/>
		</div>
	)
})

export default DomesticContractInfoSubmitForm
